import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  BusinessOutlined as BusinessIcon,
  ContactsOutlined as ContactsIcon,
  DeleteOutline as DeleteIcon,
  Inventory2Outlined as MaterialsIcon,
  NotesOutlined as NotesIcon,
  SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import { APP_SHAPE } from '../../constants/shape';
import { getFullWasteCatalogMaterialOptions, type WasteCatalogOption } from '../../services/waste-catalog.service';
import type { Supplier, SupplierContact, SupplierInput, SupplierStatus } from '../../types/supplier.type';
import DialogHeader from '../common/DialogHeader';
import { useOrganizationStore } from '../../stores/organization.store';

interface SupplierFormDialogProps {
  open: boolean;
  supplier?: Supplier | null;
  saving: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (input: SupplierInput) => Promise<boolean>;
}

const EMPTY_CONTACT: SupplierContact = { name: '', phone: '', email: '', role: '' };
const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } } as const;
const sectionSx = {
  p: { xs: 2, sm: 3 },
  borderRadius: `${APP_SHAPE.radiusLg}px`,
} as const;

function SectionTitle({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box sx={{ color: 'primary.main', display: 'flex', mt: 0.5 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </Box>
    </Box>
  );
}

export default function SupplierFormDialog({
  open,
  supplier,
  saving,
  submitError,
  onClose,
  onSubmit,
}: SupplierFormDialogProps) {
  const { t } = useTranslation();
  const organizationId = useOrganizationStore((state) => state.selectedOrganization?.id);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [name, setName] = useState('');
  const [status, setStatus] = useState<SupplierStatus>('active');
  const [contacts, setContacts] = useState<SupplierContact[]>([{ ...EMPTY_CONTACT }]);
  const [materialIds, setMaterialIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [materialOptions, setMaterialOptions] = useState<WasteCatalogOption[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialError, setMaterialError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loadMaterials = useCallback(async () => {
    setMaterialsLoading(true);
    setMaterialError(false);
    try {
      // The full catalog: these are the SUPPLIER'S waste types, not the owner
      // organization's basket -- basket scoping hid most types from recyclers.
      setMaterialOptions(await getFullWasteCatalogMaterialOptions());
    } catch {
      setMaterialError(true);
    } finally {
      setMaterialsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setName(supplier?.name ?? '');
    setStatus(supplier?.status === 'archived' ? 'active' : supplier?.status ?? 'active');
    setContacts(supplier?.contacts.length ? supplier.contacts.map((contact) => ({ ...contact })) : [{ ...EMPTY_CONTACT }]);
    setMaterialIds(supplier?.material_ids ?? []);
    setNotes(supplier?.internal_notes ?? '');
    setMaterialOptions([]);
    setSubmitted(false);
    setMaterialError(false);
    void loadMaterials();
  }, [open, supplier, loadMaterials]);

  const selectedMaterials = useMemo(
    () => materialIds.map(
      (id) => materialOptions.find((option) => option.value === id) ?? { value: id, label: id },
    ),
    [materialIds, materialOptions],
  );
  const contactErrors = contacts.map((contact) => ({
    name: submitted && !contact.name.trim(),
    method: submitted && !contact.phone?.trim() && !contact.email?.trim(),
  }));
  const isValid = name.trim().length > 0
    && contacts.every((contact) => contact.name.trim() && (contact.phone?.trim() || contact.email?.trim()))
    && materialIds.length > 0;

  const changeContact = (index: number, field: keyof SupplierContact, value: string) => {
    setContacts((current) => current.map((contact, contactIndex) => (
      contactIndex === index ? { ...contact, [field]: value } : contact
    )));
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!isValid) return;
    const success = await onSubmit({
      // Preserve a pre-existing link, but never expose controls that create or change one.
      supplier_org_id: supplier?.supplier_org_id ?? null,
      name: name.trim(),
      contacts: contacts.map((contact) => ({
        name: contact.name.trim(),
        phone: contact.phone?.trim() || undefined,
        email: contact.email?.trim() || undefined,
        role: contact.role?.trim() || undefined,
      })),
      material_ids: materialIds,
      status,
      internal_notes: notes.trim() || null,
    });
    if (success) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      scroll="paper"
      PaperProps={{ sx: { maxHeight: { sm: '92vh' }, borderRadius: { sm: `${APP_SHAPE.radiusXl}px` } } }}
    >
      <DialogHeader
        title={supplier ? t('suppliers.form.editTitle') : t('suppliers.form.addTitle')}
        subtitle={supplier ? t('suppliers.form.editSubtitle') : t('suppliers.form.addSubtitle')}
      />
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default' }}>
        <Stack spacing={3}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Paper variant="outlined" sx={sectionSx}>
            <SectionTitle
              icon={<BusinessIcon fontSize="small" />}
              title={t('suppliers.form.detailsSection.title')}
              subtitle={t('suppliers.form.detailsSection.subtitle')}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
              <TextField
                label={t('suppliers.form.name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                error={submitted && !name.trim()}
                helperText={submitted && !name.trim() ? t('suppliers.form.nameError') : t('suppliers.form.nameHelper')}
                sx={fieldSx}
              />
              <FormControl sx={fieldSx}>
                <InputLabel id="supplier-status-label">{t('suppliers.form.status')}</InputLabel>
                <Select
                  labelId="supplier-status-label"
                  label={t('suppliers.form.status')}
                  value={status}
                  onChange={(event) => setStatus(event.target.value as SupplierStatus)}
                >
                  <MenuItem value="active">{t('suppliers.status.active')}</MenuItem>
                  <MenuItem value="paused">{t('suppliers.status.paused')}</MenuItem>
                  <MenuItem value="blocked">{t('suppliers.status.blocked')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={sectionSx}>
            <SectionTitle
              icon={<MaterialsIcon fontSize="small" />}
              title={t('suppliers.form.materialsSection.title')}
              subtitle={t('suppliers.form.materialsSection.subtitle')}
            />
            {materialError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={<Button color="inherit" size="small" onClick={() => void loadMaterials()}>{t('suppliers.details.retry')}</Button>}
              >
                {t('suppliers.form.materialsLoadError')}
              </Alert>
            )}
            <Autocomplete
              multiple
              options={materialOptions}
              value={selectedMaterials}
              loading={materialsLoading}
              disabled={materialsLoading || (materialError && materialOptions.length === 0)}
              onChange={(_, value) => setMaterialIds(value.map((option) => option.value))}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('suppliers.form.materialsLabel')}
                  placeholder={materialIds.length ? undefined : t('suppliers.form.materialsPlaceholder')}
                  required
                  error={submitted && materialIds.length === 0}
                  helperText={submitted && materialIds.length === 0 ? t('suppliers.form.materialsError') : undefined}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {materialsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Paper>

          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <SectionTitle
                icon={<ContactsIcon fontSize="small" />}
                title={t('suppliers.form.contactsSection.title')}
                subtitle={t('suppliers.form.contactsSection.subtitle')}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setContacts((current) => [...current, { ...EMPTY_CONTACT }])}
                sx={{ flexShrink: 0 }}
              >
                {t('suppliers.form.addContact')}
              </Button>
            </Box>
            <Stack spacing={3} divider={<Divider flexItem />}>
              {contacts.map((contact, index) => (
                <Box key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>{t('suppliers.form.contactLabel', { index: index + 1 })}</Typography>
                    {contacts.length > 1 && (
                      <IconButton
                        color="error"
                        aria-label={t('suppliers.form.removeContact', { index: index + 1 })}
                        onClick={() => setContacts((current) => current.filter((_, contactIndex) => contactIndex !== index))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    <TextField
                      label={t('suppliers.form.contactName')}
                      value={contact.name}
                      onChange={(event) => changeContact(index, 'name', event.target.value)}
                      required
                      error={contactErrors[index].name}
                      helperText={contactErrors[index].name ? t('suppliers.form.contactNameError') : ' '}
                      sx={fieldSx}
                    />
                    <TextField
                      label={t('suppliers.form.role')}
                      value={contact.role ?? ''}
                      onChange={(event) => changeContact(index, 'role', event.target.value)}
                      placeholder={t('suppliers.form.rolePlaceholder')}
                      helperText={t('suppliers.form.optional')}
                      sx={fieldSx}
                    />
                    <TextField
                      label={t('suppliers.form.phone')}
                      value={contact.phone ?? ''}
                      onChange={(event) => changeContact(index, 'phone', event.target.value)}
                      error={contactErrors[index].method}
                      helperText={contactErrors[index].method ? t('suppliers.form.contactMethodError') : t('suppliers.form.phoneHelper')}
                      sx={fieldSx}
                    />
                    <TextField
                      label={t('suppliers.form.email')}
                      type="email"
                      value={contact.email ?? ''}
                      onChange={(event) => changeContact(index, 'email', event.target.value)}
                      error={contactErrors[index].method}
                      helperText=" "
                      sx={fieldSx}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={sectionSx}>
            <SectionTitle
              icon={<NotesIcon fontSize="small" />}
              title={t('suppliers.form.notesSection.title')}
              subtitle={t('suppliers.form.notesSection.subtitle')}
            />
            <TextField
              fullWidth
              label={t('suppliers.form.notes')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={3}
              sx={fieldSx}
            />
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={saving}>{t('suppliers.form.cancel')}</Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
          onClick={() => void handleSubmit()}
          disabled={saving || materialsLoading}
        >
          {saving ? t('suppliers.form.saving') : supplier ? t('suppliers.form.save') : t('suppliers.form.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
